import { ISO_DATE_REGEX } from 'src/constants/dashboard'

export const RELATIVE_DATE_THRESHOLD_DAYS = 7

export interface RelativeTimeProps {
  value: number
  unit: Intl.RelativeTimeFormatUnit
}

const UNITS: Array<{ threshold: number; unit: Intl.RelativeTimeFormatUnit }> = [
  { threshold: 60, unit: 'second' },
  { threshold: 3600, unit: 'minute' },
  { threshold: 86400, unit: 'hour' },
  { threshold: Infinity, unit: 'day' }
]

const DIVISORS: Partial<Record<Intl.RelativeTimeFormatUnit, number>> = {
  day: 86400,
  hour: 3600,
  minute: 60,
  second: 1
}

export const getRelativeTimeProps = (dateString: string): RelativeTimeProps => {
  const now = Date.now()
  const date = new Date(dateString).getTime()
  const diffInSeconds = Math.floor((date - now) / 1000)

  const absDiff = Math.abs(diffInSeconds)
  const { unit } = UNITS.find(({ threshold }) => absDiff < threshold)!
  const divisor = DIVISORS[unit] ?? 1

  return {
    value: Math.floor(diffInSeconds / divisor),
    unit
  }
}

export const formatDate = (date: string): string => {
  return new Date(date).toLocaleString()
}

export const shouldUseRelativeTime = (
  dateString: string,
  thresholdDays: number = RELATIVE_DATE_THRESHOLD_DAYS
): boolean => {
  const relativeProps = getRelativeTimeProps(dateString)
  return (
    Math.abs(relativeProps.value) <= thresholdDays ||
    relativeProps.unit !== 'day'
  )
}

const UNIT_SHORT: Record<string, string> = {
  second: 's',
  minute: 'min',
  hour: 'h',
  day: 'd',
  week: 'sem',
  month: 'mes',
  year: 'a'
}

export const formatRelativeTimeShort = (props: {
  value: number
  unit: string
}): string => {
  const abs = Math.abs(Math.round(props.value))
  const short = UNIT_SHORT[props.unit] ?? props.unit
  return `${abs}${short}`
}

export const formatIfIsoDate = (value: string, locale?: string): string => {
  if (typeof value === 'string' && ISO_DATE_REGEX.test(value)) {
    const date = new Date(value)
    if (isNaN(date.getTime())) return value
    const formattedDate = new Intl.DateTimeFormat(locale, {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit'
    }).format(date)

    const formattedTime = new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)

    return `${formattedDate} ${formattedTime}`
  }

  return value
}
